<script setup>
import { useI18n } from 'vue-i18n';
import NucleoWorkOrderRequestIndicator from './NucleoWorkOrderRequestIndicator.vue';

defineProps({
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['open']);

const { t } = useI18n();
const fallback = value => value || t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED');
const address = row =>
  row.imovel?.logradouro ||
  row.imovel?.title ||
  t('NUCLEO_WORK_ORDERS.TABLE.ADDRESS_UNAVAILABLE');
const postSaleUser = row => row.responsavel_pos_venda || row.solicitante;
const formatDate = value => {
  if (!value) return t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED');
  return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
};
</script>

<template>
  <div class="divide-y divide-lais-border-soft lg:hidden">
    <article
      v-for="row in rows"
      :key="row.id"
      class="cursor-pointer space-y-4 p-4 hover:bg-lais-primary-50/30"
      :class="row.status_visualizacao_atual === 0 ? 'font-bold' : ''"
      :aria-label="
        row.status_visualizacao_atual === 0
          ? t('NUCLEO_WORK_ORDERS.TABLE.NEW_REQUEST')
          : undefined
      "
      @click="emit('open', row)"
    >
      <div>
        <p class="text-xs uppercase tracking-wide text-lais-text-muted">
          {{ fallback(postSaleUser(row)?.name) }}
        </p>
        <p
          class="text-lais-text"
          :class="
            row.status_visualizacao_atual === 0 ? 'font-bold' : 'font-semibold'
          "
        >
          {{ address(row) }}
        </p>
        <span v-if="row.status_visualizacao_atual === 0" class="sr-only">
          {{ t('NUCLEO_WORK_ORDERS.TABLE.NEW_REQUEST') }}
        </span>
        <p class="text-xs text-lais-text-muted">
          {{
            [row.imovel?.bairro, row.imovel?.cidade, row.imovel?.uf]
              .filter(Boolean)
              .join(' · ')
          }}
        </p>
      </div>
      <dl class="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.TABLE.STORE') }}
          </dt>
          <dd>{{ fallback(row.loja?.name) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.TABLE.MANAGER') }}
          </dt>
          <dd>{{ fallback(row.gerente?.name) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.TABLE.REQUESTED_AT') }}
          </dt>
          <dd>{{ formatDate(row.data_solicitacao) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.TABLE.DUE_AT') }}
          </dt>
          <dd>{{ formatDate(row.data_previsao) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.TABLE.ONUS') }}
          </dt>
          <dd class="mt-1">
            <NucleoWorkOrderRequestIndicator :value="row.onus_solicitada" />
          </dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.TABLE.DISTRICT') }}
          </dt>
          <dd class="mt-1">
            <NucleoWorkOrderRequestIndicator :value="row.vendedor_comarca" />
          </dd>
        </div>
      </dl>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="service in row.servicos"
          :key="service.id"
          type="button"
          class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-xs font-semibold text-lais-primary"
          @click.stop="emit('open', { ...row, selectedServiceId: service.id })"
        >
          {{ service.nome }}
        </button>
      </div>
      <button
        type="button"
        class="w-full rounded-lais bg-lais-primary px-3 py-2 text-sm font-semibold text-white transition hover:bg-lais-primary/90"
        @click.stop="emit('open', row)"
      >
        {{ t('NUCLEO_WORK_ORDERS.ACTIONS.VIEW_DETAILS') }}
      </button>
    </article>
  </div>
</template>
